import EditableContentBlock from "../../../../EditableContentBlock";
import { EditCompanyFormValues } from "../../../../EditCompanyForms/EditCompanyFormValues";
import CheckFrequenciesFields from "./EditCheckFrequenciesFields";
import SubmitOrCancelButtonBar from "../../../../ReusableComponents/SubmitOrCancelButtonBar/SubmitOrCancelButtonBar";
import useEditCheckFrequenciesForm from "./useEditCheckFrequenciesForm";
import { EditCheckFrequenciesFormValues } from "./useEditCheckFrequenciesForm";
import { defaultRiskCategories } from "../EditRiskCategoriesForm/useEditRiskCategoriesForm";
import { CheckSettings } from "../../../../../models/api/admin/company";
import { useState } from "react";
import ConfirmationDialog from "../../../../ReusableComponents/ConfirmationDialog";
import { useCompany } from "../../../../../api/admin/company";
import { useParams } from "react-router-dom";

type PageParams = {
    id: string;
};

const DEFAULT_CHECK_FREQUENCIES_DATA: {
    isOpen: boolean;
    callback?: () => void;
} = { isOpen: false, callback: undefined };

interface EditCheckFrequenciesFormProps {
    initialValues: EditCompanyFormValues;
    handleEditCompany: (
        data: EditCompanyFormValues,
        resetFormCallback: () => void
    ) => void;
    isSubmitting?: boolean;
    canEdit: boolean;
}

export default function EditCheckFrequenciesForm({
    initialValues,
    handleEditCompany,
    isSubmitting,
    canEdit,
}: EditCheckFrequenciesFormProps) {
    const { id } = useParams<PageParams>();

    const useCompanyReturn = useCompany(id);

    const { isEditingCompany, refetchCompany } = useCompanyReturn;

    const { methods, displayedValues } =
        useEditCheckFrequenciesForm(initialValues);

    const resetAndCloseForm = (setIsEdit?: (isEdit: boolean) => void) => {
        methods.reset(displayedValues);
        if (typeof setIsEdit === "function") setIsEdit(false);
    };

    const [
        checkFrequenciesWarningModalData,
        setCheckFrequenciesWarningModalData,
    ] = useState(DEFAULT_CHECK_FREQUENCIES_DATA);

    const handleEditableContentSubmit = (
        handleEditCheckFrequencies: (
            data: EditCheckFrequenciesFormValues
        ) => void,
        isDirty: boolean
    ) => {
        if (isDirty) {
            setCheckFrequenciesWarningModalData({
                isOpen: true,
                callback: () => {
                    methods.handleSubmit(handleEditCheckFrequencies)();

                    /** Close modal and clear callback */
                    setCheckFrequenciesWarningModalData(
                        DEFAULT_CHECK_FREQUENCIES_DATA
                    );
                },
            });

            return;
        }

        /** If form not dirty, close modal and clear callback */
        setCheckFrequenciesWarningModalData(DEFAULT_CHECK_FREQUENCIES_DATA);
    };

    return (
        <EditableContentBlock
            title="Check Frequencies"
            onClosed={resetAndCloseForm}
            disableEdit={!canEdit}
        >
            {({ isEdit, setIsEdit }) => {
                const { isOpen, callback } = checkFrequenciesWarningModalData;

                const {
                    getValues,
                    formState: { dirtyFields },
                } = methods;

                const checkFrequency = getValues().checkFrequency;
                const originalCheckFrequency = displayedValues.checkFrequency;

                /** Compares edited fields against form state, and returns desciptive strings. */
                const formattedFieldValues = Object.entries(
                    checkFrequency
                ).reduce((acc: string[], [key, value]) => {
                    const field = key as "range1" | "range2" | "range3",
                        isEdited = !!dirtyFields.checkFrequency?.[field];

                    if (isEdited) {
                        const originalValue = originalCheckFrequency[field];
                        if (key === "range1")
                            return [
                                ...acc,
                                `Range 1: ${originalValue} → ${value} months`,
                            ];
                        if (key === "range2")
                            return [
                                ...acc,
                                `Range 2: ${originalValue} → ${value} months`,
                            ];
                        if (key === "range3")
                            return [
                                ...acc,
                                `Range 3: ${originalValue} → ${value} months`,
                            ];
                        return acc;
                    } else {
                        return acc;
                    }
                }, []);

                return (
                    <>
                        <ConfirmationDialog
                            open={isOpen}
                            variant="danger"
                            title="Warning!"
                            caption={[
                                "You have made the following edits:",
                                ...formattedFieldValues,
                                "This action will update existing drivers in these ranges, and may take some time. An email will be sent out, click confirm to continue.",
                            ]}
                            onClose={() =>
                                setCheckFrequenciesWarningModalData(
                                    DEFAULT_CHECK_FREQUENCIES_DATA
                                )
                            }
                            onConfirm={() => {
                                if (!isEditingCompany) {
                                    callback?.();
                                    refetchCompany();
                                }
                            }}
                        />

                        <div className="p-6">
                            {!isEdit && nonEditableContent()}

                            {isEdit && editableContent(setIsEdit)}
                        </div>
                    </>
                );
            }}
        </EditableContentBlock>
    );

    function constructNewRiskProfiles(data: EditCheckFrequenciesFormValues) {
        const { range1, range2, range3 } = data.checkFrequency;

        const existingRiskProfiles = initialValues?.companyRiskProfiles;

        const range1profile = existingRiskProfiles?.find(
            (profile) => profile.severityLevel === 1
        );
        const range2profile = existingRiskProfiles?.find(
            (profile) => profile.severityLevel === 2
        );
        const range3profile = existingRiskProfiles?.find(
            (profile) => profile.severityLevel === 3
        );
        const newRiskProfiles: CheckSettings["companyRiskProfiles"] = [
            {
                severityLevel: 1,
                // NB: Api requires all 4 values be given
                // so giving default checkFrequency/name values if they are not set
                name: range1profile?.name ?? defaultRiskCategories.range1Name,
                lowerThreshold:
                    range1profile?.lowerThreshold ??
                    defaultRiskCategories.range1,
                checkFrequency: range1,
            },
            {
                severityLevel: 2,
                name: range2profile?.name ?? defaultRiskCategories.range2Name,
                lowerThreshold:
                    range2profile?.lowerThreshold ??
                    defaultRiskCategories.range2,
                checkFrequency: range2,
            },
            {
                severityLevel: 3,
                name: range3profile?.name ?? defaultRiskCategories.range3Name,
                lowerThreshold:
                    range3profile?.lowerThreshold ??
                    defaultRiskCategories.range3,
                checkFrequency: range3,
            },
        ];
        return { companyRiskProfiles: newRiskProfiles };
    }

    function editableContent(setIsEdit: (isEdit: boolean) => void) {
        function handleEditCheckFrequencies(
            data: EditCheckFrequenciesFormValues
        ) {
            const formattedData = constructNewRiskProfiles(data);
            handleEditCompany(formattedData, () =>
                resetAndCloseForm(setIsEdit)
            );
        }

        const onSumbit = () => {
            handleEditableContentSubmit(
                handleEditCheckFrequencies,
                methods.formState.isDirty
            );
        };

        return (
            <form>
                <CheckFrequenciesFields methods={methods} />
                <div className="pt-7" />
                <SubmitOrCancelButtonBar
                    type="button"
                    disabled={!methods.formState.isDirty}
                    onSubmit={onSumbit}
                    onCancel={() => resetAndCloseForm(setIsEdit)}
                    isLoading={isSubmitting}
                />
            </form>
        );
    }

    function nonEditableContent() {
        return (
            <div className="space-y-2">
                <p>
                    <b>Range 1 check every: </b>
                    {displayedValues?.checkFrequency?.range1} months
                </p>
                <p>
                    <b>Range 2 check every: </b>
                    {displayedValues?.checkFrequency?.range2} months
                </p>
                <p>
                    <b>Range 3 check every: </b>
                    {displayedValues?.checkFrequency?.range3} months
                </p>
            </div>
        );
    }
}
